const SWITCH_DELAY = 180; // hover intent when moving from one open item to another
const CLOSE_DELAY = 250;  // grace period when the pointer leaves trigger/panel
const FADE_MS = 170;      // matches the panel transition in header-v3.css
const DESKTOP_QUERY = '(min-width: 1280px)'; // Tailwind xl, where this header is shown

// Desktop mega-menu: hover-intent dropdowns, side-rail tabs, keyboard support
// and the region/language popover. State is expressed as classes and ARIA on
// the markup; layout and transitions are CSS.
export default class HeaderV3Desktop {
    constructor(element, header) {
        this.el = element;
        this.header = header;
        this.items = Array.from(element.querySelectorAll('[data-hv3-item]'))
            .filter((item) => item.querySelector('[data-hv3-panel]'));
        this.region = element.querySelector('[data-hv3-region]');

        this.openItem = null;
        this.closingItem = null;
        this.closeTimer = null;
        this.switchTimer = null;
        this.touch = false;

        this.items.forEach((item) => this.bindItem(item));
        this.bindRegion();
        this.bindGlobal();
    }

    // ---- Dropdowns ---------------------------------------------------------

    bindItem(item) {
        const trigger = item.querySelector('[data-hv3-trigger]');
        const panel = item.querySelector('[data-hv3-panel]');

        trigger.addEventListener('mouseenter', () => {
            if (this.touch) return;
            this.cancelClose();
            clearTimeout(this.switchTimer);
            if (this.openItem && this.openItem !== item) {
                this.switchTimer = setTimeout(() => this.open(item), SWITCH_DELAY);
            } else {
                this.open(item);
            }
        });

        trigger.addEventListener('mouseleave', () => {
            clearTimeout(this.switchTimer);
            this.scheduleClose();
        });

        trigger.addEventListener('click', (event) => {
            const isOpen = this.openItem === item;
            if (trigger.tagName === 'BUTTON') {
                event.preventDefault();
                isOpen ? this.close() : this.open(item);
                return;
            }
            if (this.touch && !isOpen) {
                event.preventDefault();
                this.open(item);
            }
        });

        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown' || event.key === ' ' || (event.key === 'Enter' && trigger.tagName === 'BUTTON')) {
                event.preventDefault();
                this.open(item);
                this.focusFirst(panel);
            }
        });

        panel.addEventListener('mouseenter', () => this.cancelClose());
        panel.addEventListener('mouseleave', () => this.scheduleClose());

        // Keyboard users tabbing out of the item: close it. relatedTarget is
        // null for clicks on non-focusable areas inside the panel, so that case
        // is left alone.
        item.addEventListener('focusout', (event) => {
            if (event.relatedTarget && !item.contains(event.relatedTarget) && this.openItem === item) {
                this.close();
            }
        });

        this.bindTabs(item, panel);
    }

    bindTabs(item, panel) {
        const tabs = Array.from(panel.querySelectorAll('[data-hv3-tab]'));
        if (!tabs.length) return;

        tabs.forEach((tab, index) => {
            tab.addEventListener('mouseenter', () => {
                if (!this.touch) this.activateTab(panel, tab);
            });
            tab.addEventListener('focus', () => this.activateTab(panel, tab));
            tab.addEventListener('click', () => this.activateTab(panel, tab));
            tab.addEventListener('keydown', (event) => {
                let next = null;
                if (event.key === 'ArrowDown') next = tabs[(index + 1) % tabs.length];
                if (event.key === 'ArrowUp') next = tabs[(index - 1 + tabs.length) % tabs.length];
                if (next) {
                    event.preventDefault();
                    next.focus();
                    return;
                }
                if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    const pane = panel.querySelector('[data-hv3-pane].is-active');
                    const first = pane && pane.querySelector('a');
                    if (first) first.focus();
                }
            });
        });
    }

    activateTab(panel, tab) {
        const id = tab.dataset.hv3Tab;

        panel.querySelectorAll('[data-hv3-tab]').forEach((other) => {
            const active = other === tab;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-selected', active ? 'true' : 'false');
            other.setAttribute('tabindex', active ? '0' : '-1');
        });

        panel.querySelectorAll('[data-hv3-pane]').forEach((pane) => {
            pane.classList.toggle('is-active', pane.dataset.hv3Pane === id);
        });

        // Banners: the section's own set, else the dropdown's default set.
        const sets = Array.from(panel.querySelectorAll('.hv3-banners[data-hv3-for]'));
        if (sets.length) {
            const own = sets.find((set) => set.dataset.hv3For === id);
            const fallback = sets.find((set) => set.dataset.hv3For === 'default');
            sets.forEach((set) => set.classList.toggle('is-active', set === (own || fallback)));
        }
    }

    open(item) {
        clearTimeout(this.switchTimer);
        this.cancelClose();
        this.closeRegion();

        if (this.openItem === item) return;

        // Switching between items keeps the header's open count at one, and
        // swaps panels instantly — only a real close fades out.
        if (this.openItem) {
            this.setItemOpen(this.openItem, false, true);
        } else {
            this.header.setOpen(true);
        }
        if (this.closingItem && this.closingItem !== item) {
            this.setItemOpen(this.closingItem, false, true);
        }

        this.openItem = item;
        this.setItemOpen(item, true);
    }

    close() {
        clearTimeout(this.switchTimer);
        this.cancelClose();
        if (!this.openItem) return;

        this.setItemOpen(this.openItem, false);
        this.openItem = null;
        this.header.setOpen(false);
    }

    // A closed panel is display:none (so its images are not fetched). Opening
    // shows it first and starts the fade on the next frame; closing fades out,
    // then hides — unless `immediate`, which hides at once (used when another
    // item takes over). At most one item is ever mid-fade (this.closingItem).
    setItemOpen(item, open, immediate = false) {
        const panel = item.querySelector('[data-hv3-panel]');
        clearTimeout(item.hv3HideTimer);
        if (this.closingItem === item) this.closingItem = null;
        item.querySelector('[data-hv3-trigger]').setAttribute('aria-expanded', open ? 'true' : 'false');

        if (open) {
            item.classList.add('is-open');
            void panel.offsetWidth; // commit display:block before the transition starts
            item.classList.add('is-visible');
            return;
        }

        item.classList.remove('is-visible');
        if (immediate) {
            item.classList.remove('is-open');
            return;
        }

        this.closingItem = item;
        item.hv3HideTimer = setTimeout(() => {
            item.classList.remove('is-open');
            if (this.closingItem === item) this.closingItem = null;
        }, FADE_MS);
    }

    scheduleClose() {
        this.cancelClose();
        this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    cancelClose() {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
    }

    focusFirst(panel) {
        const target = panel.querySelector('[data-hv3-tab].is-active') || panel.querySelector('a, button');
        if (target) target.focus();
    }

    // ---- Region / language popover -----------------------------------------

    bindRegion() {
        if (!this.region) return;

        this.regionTrigger = this.region.querySelector('[data-hv3-region-trigger]');
        this.regionTrigger.addEventListener('click', () => {
            this.region.classList.contains('is-open') ? this.closeRegion() : this.openRegion();
        });

        // Picking a region keeps the popover open briefly so the change reads.
        this.header.root.addEventListener('hv3:region-change', () => {
            if (this.region.classList.contains('is-open')) {
                setTimeout(() => this.closeRegion(), 220);
            }
        });

        this.region.addEventListener('focusout', (event) => {
            if (event.relatedTarget && !this.region.contains(event.relatedTarget)) {
                this.closeRegion();
            }
        });
    }

    openRegion() {
        if (!this.region || this.region.classList.contains('is-open')) return;
        this.close();
        this.region.classList.add('is-open');
        this.regionTrigger.setAttribute('aria-expanded', 'true');
        this.header.setOpen(true);
    }

    closeRegion(refocus = false) {
        if (!this.region || !this.region.classList.contains('is-open')) return;
        this.region.classList.remove('is-open');
        this.regionTrigger.setAttribute('aria-expanded', 'false');
        this.header.setOpen(false);
        if (refocus) this.regionTrigger.focus();
    }

    // ---- Document-level ----------------------------------------------------

    bindGlobal() {
        // Input modality, from every pointer interaction over the header (not
        // just a trigger's pointerdown), so a hybrid device switching between
        // touch and mouse is always classified by its latest pointer. Pointer
        // events precede the compatibility mouse events, so `this.touch` is
        // current by the time a mouseenter handler reads it. Touch (iPad at
        // desktop width): the first tap opens, the second follows the link;
        // hover emulation is ignored so a tap never opens and navigates at once.
        const setModality = (event) => {
            this.touch = event.pointerType === 'touch';
        };
        this.el.addEventListener('pointerover', setModality);
        this.el.addEventListener('pointerdown', setModality);

        // Leaving the nav for the logo or the right-hand controls closes at once.
        this.el.querySelectorAll('.hv3-logo, .hv3-right').forEach((zone) => {
            zone.addEventListener('mouseenter', () => {
                if (!this.touch) this.close();
            });
        });

        document.addEventListener('click', (event) => {
            if (this.openItem && !this.openItem.contains(event.target)) this.close();
            if (this.region && !this.region.contains(event.target)) this.closeRegion();
        });

        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;
            if (this.region && this.region.classList.contains('is-open')) {
                this.closeRegion(true);
                return;
            }
            if (this.openItem) {
                const trigger = this.openItem.querySelector('[data-hv3-trigger]');
                this.close();
                trigger.focus();
            }
        });

        // Crossing the breakpoint hides this header; nothing should stay open.
        const media = window.matchMedia(DESKTOP_QUERY);
        media.addEventListener('change', (event) => {
            if (!event.matches) {
                this.close();
                this.closeRegion();
            }
        });
    }
}
